import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  Space, 
  Modal, 
  Descriptions,
  message,
  Spin,
  Pagination
} from 'antd';
import { 
  SearchOutlined, 
  EyeOutlined, 
  DownloadOutlined, 
  FilterOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  ShoppingCartOutlined
} from '@ant-design/icons';
import moment from 'moment';
import apiClient from '../../services/apiClient';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    fetchOrdersAndStats();
  }, [pagination.current, pagination.pageSize, filters]);

  const fetchOrdersAndStats = async () => {
    setLoading(true);
    try {
      // Single API call to fetch all orders
      const response = await apiClient.get('http://localhost:8080/api/v1/sb/razorpay/orders', {
        params: {
          count: 100, // Get more data for client-side filtering and stats
          skip: 0
        }
      });
      const allOrders = response.data || [];
      
      // Calculate statistics from all orders
      const totalAmount = allOrders.reduce((sum, order) => {
        return sum + (order.amount || 0);
      }, 0);
      
      const ordersByStatus = allOrders.reduce((acc, order) => {
        const status = order.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      
      const recentOrders = allOrders.filter(order => {
        const orderDate = new Date(order.created_at * 1000); // Razorpay uses Unix timestamp
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return orderDate >= thirtyDaysAgo;
      }).length;
      
      // Update statistics
      setStats({
        totalAmount: totalAmount / 100, // Convert from paise to rupees
        totalOrders: allOrders.length,
        recentOrders,
        ordersByStatus
      });
      
      // Apply client-side filtering for table
      let filteredOrders = allOrders;
      
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        filteredOrders = filteredOrders.filter(order => 
          order.id?.toLowerCase().includes(searchTerm) ||
          order.notes?.notes?.toLowerCase().includes(searchTerm) ||
          order.notes?.customer_name?.toLowerCase().includes(searchTerm) ||
          order.notes?.customer_email?.toLowerCase().includes(searchTerm) ||
          order.receipt?.toLowerCase().includes(searchTerm)
        );
      }
      
      if (filters.status) {
        filteredOrders = filteredOrders.filter(order => 
          order.status === filters.status
        );
      }
      
      if (filters.startDate && filters.endDate) {
        filteredOrders = filteredOrders.filter(order => {
          const orderDate = new Date(order.created_at * 1000);
          const startDate = new Date(filters.startDate);
          const endDate = new Date(filters.endDate);
          return orderDate >= startDate && orderDate <= endDate;
        });
      }
      
      // Apply pagination to filtered results
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const paginatedOrders = filteredOrders.slice(startIndex, endIndex);
      
      setOrders(paginatedOrders);
      setPagination(prev => ({
        ...prev,
        total: filteredOrders.length
      }));
    } catch (error) {
      message.error('Failed to fetch orders');
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setModalVisible(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'green';
      case 'attempted':
        return 'blue';
      case 'created':
        return 'blue';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      render: (id) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {id}
        </span>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount, record) => (
        <span>₹{(amount / 100).toFixed(2)} {record.currency}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Receipt',
      dataIndex: 'receipt',
      key: 'receipt',
      width: 120,
      render: (receipt) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {receipt || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (timestamp) => moment.unix(timestamp).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EyeOutlined />} 
            onClick={() => handleViewOrder(record)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
  };

  const handleSearch = (value) => {
    handleFilterChange('search', value);
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2>Order Management</h2>
      
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Total Amount"
              value={stats.totalAmount || 0}
              prefix="₹"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Total Orders"
              value={stats.totalOrders || 0}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Recent (30 days)"
              value={stats.recentOrders || 0}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Paid"
              value={stats.ordersByStatus?.paid || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Created"
              value={stats.ordersByStatus?.created || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={4}>
          <Card>
            <Statistic
              title="Failed"
              value={stats.ordersByStatus?.failed || 0}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Search by Order ID, Receipt, Notes, Customer Name, or Email"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onPressEnter={(e) => handleSearch(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={4}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="">All</Option>
              <Option value="paid">Paid</Option>
              <Option value="created">Created</Option>
              <Option value="attempted">Attempted</Option>
              <Option value="failed">Failed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange('startDate', dates[0]?.format('YYYY-MM-DDTHH:mm:ss'));
                  handleFilterChange('endDate', dates[1]?.format('YYYY-MM-DDTHH:mm:ss'));
                } else {
                  handleFilterChange('startDate', null);
                  handleFilterChange('endDate', null);
                }
              }}
            />
          </Col>
          <Col xs={24} sm={3}>
            <Button 
              type="primary" 
              icon={<FilterOutlined />}
              onClick={() => {
                setPagination(prev => ({ ...prev, current: 1 }));
                fetchOrdersAndStats();
              }}
            >
              Apply Filters
            </Button>
          </Col>
          <Col xs={24} sm={3}>
            <Button 
              onClick={() => {
                setFilters({
                  search: '',
                  status: '',
                  startDate: null,
                  endDate: null
                });
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Orders Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} orders`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Order Details Modal */}
      <Modal
        title="Order Details"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedOrder && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Order ID" span={2}>
              <span style={{ fontFamily: 'monospace' }}>{selectedOrder.id}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              ₹{(selectedOrder.amount / 100).toFixed(2)} {selectedOrder.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(selectedOrder.status)}>
                {selectedOrder.status?.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Receipt">
              {selectedOrder.receipt || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Notes">
              {selectedOrder.notes?.notes || 'N/A'}
            </Descriptions.Item>
            {selectedOrder.notes?.customer_name && (
              <Descriptions.Item label="Customer Name">
                {selectedOrder.notes.customer_name}
              </Descriptions.Item>
            )}
            {selectedOrder.notes?.customer_email && (
              <Descriptions.Item label="Customer Email">
                {selectedOrder.notes.customer_email}
              </Descriptions.Item>
            )}
            {selectedOrder.notes?.customer_phone && (
              <Descriptions.Item label="Customer Phone">
                {selectedOrder.notes.customer_phone}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Created At">
              {moment.unix(selectedOrder.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Attempts">
              {selectedOrder.attempts || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Amount Paid">
              ₹{selectedOrder.amount_paid ? (selectedOrder.amount_paid / 100).toFixed(2) : '0.00'}
            </Descriptions.Item>
            <Descriptions.Item label="Amount Due">
              ₹{selectedOrder.amount_due ? (selectedOrder.amount_due / 100).toFixed(2) : '0.00'}
            </Descriptions.Item>
            <Descriptions.Item label="Currency">
              {selectedOrder.currency}
            </Descriptions.Item>
            <Descriptions.Item label="Partial Payment">
              {selectedOrder.partial_payment ? 'Yes' : 'No'}
            </Descriptions.Item>
            {selectedOrder.customer_id && (
              <Descriptions.Item label="Customer ID">
                {selectedOrder.customer_id}
              </Descriptions.Item>
            )}
            {selectedOrder.entity && (
              <Descriptions.Item label="Entity">
                {selectedOrder.entity}
              </Descriptions.Item>
            )}
            {selectedOrder.offers && selectedOrder.offers.length > 0 && (
              <Descriptions.Item label="Offers" span={2}>
                {selectedOrder.offers.map((offer, index) => (
                  <div key={index}>
                    <strong>{offer.id}</strong> - {offer.type}
                  </div>
                ))}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AdminOrders;
